import { Reducer, Action } from 'redux';
import {
  ACTION, ReceiveLanguageOptions,
  RequestExternalUrlGroup, ReceiveExternalUrlGroup, FailExternalUrlGroup,
  RequestDeleteExternalUrlGroup, ReceiveDeleteExternalUrlGroup, FailDeleteExternalUrlGroup,
  RequestSaveExternalUrlGroup, ReceiveSaveExternalUrlGroup, FailSaveExternalUrlGroup,
  UpdateItem, ChangeLanguage, CopyLanguageToAll,
  ClearForm, UpdateValidation,
  ChangeLongDescription,
  CopyLongDescriptionToAll,
  UpdateAccessPolicyItem,
  RemoveAccessPolicy,
  RemoveAllAccessPolicies,
  AddAccessPolicy,
  UpdateAccessPolicyValidation,
  ResetAccessPolicyClientValidation,
  ResetAccessPolicyServerValidation,
  ResetAllAccessPolicyValidation,
  RequestAddExternalUrls,
  ReceiveAddExternalUrls,
  FailAddExternalUrls,
  UpdateExternalUrlItem,
  DeleteExternalUrl,
  DeleteAllExternalUrls,
  UpdateExternalUrlsValidation,
  RequestExternalUrlOptions,
  ReceiveExternalUrlOptions,
  FailExternalUrlOptions
} from './action-types';
import { languageValidationParameter, longDescriptionValidationParameter } from './constants';
import {
  ExternalUrlGroupsFormState as ExternalUrlGroupsFormState, ExternalUrlGroupItem
} from './types-store';
import { createId, createInitialAccessPolicy } from 'app/area/external-url-groups/form/utils/helpers';
import { withOptionsReducer } from 'app/components/options-handler';
import { STATE } from 'app/constants';
import {
  clearValidationForKeys, clearValidationForAllParameters, clearValidationForSingleParameter
} from 'app/area/external-url-groups/form/utils/validation';
import { AddExternalUrlType } from 'app/area/external-url-groups/form/types';

export const INITIAL_STATE: ExternalUrlGroupsFormState = {
  actionType: undefined,
  externalUrlGroupItem: undefined,
  originalExternalUrlGroupItem: undefined,
  fetching: false,
  deleting: false,
  saving: false,
  languageOptions: [],
  externalUrlOptions: {
    fetching: undefined
  },
  validation: {},
  accessPolicyValidation: {},
  exterenalUrlsValidation: {},
  fetchingAddExternalUrls: false
};

const reducers: { [indexer: string]: Reducer<ExternalUrlGroupsFormState, Action> } = {

  [ACTION.RECEIVE_LANGUAGE_OPTIONS]: (state: ExternalUrlGroupsFormState,
    action: ReceiveLanguageOptions): ExternalUrlGroupsFormState => {

    return {
      ...state,
      languageOptions: action.options
    };
  },

  [ACTION.REQUEST_EXTERNAL_URL_OPTIONS]: (state: ExternalUrlGroupsFormState,
    action: RequestExternalUrlOptions): ExternalUrlGroupsFormState => {
    return {
      ...state,
      externalUrlOptions: {
        ...(state.externalUrlOptions || {}),
        query: action.query,
        fetching: true,
        loadOptionsParams: action.loadOptionsParams,
        values: undefined
      }
    };
  },

  [ACTION.RECEIVE_EXTERNAL_URL_OPTIONS]: (state: ExternalUrlGroupsFormState,
    action: ReceiveExternalUrlOptions): ExternalUrlGroupsFormState => {
    if (!state.externalUrlOptions || state.externalUrlOptions.query !== action.query) {
      return state;
    }

    return {
      ...state,
      externalUrlOptions: {
        ...(state.externalUrlOptions || {}),
        query: action.query,
        values: action.values,
        fetching: false
      }
    };
  },

  [ACTION.FAIL_EXTERNAL_URL_OPTIONS]: (state: ExternalUrlGroupsFormState,
    action: FailExternalUrlOptions): ExternalUrlGroupsFormState => {

    if (!state.externalUrlOptions || state.externalUrlOptions.query !== action.query) {
      return state;
    }

    return {
      ...state,
      externalUrlOptions: {
        ...(state.externalUrlOptions || {}),
        fetching: false
      }
    };
  },

  [ACTION.REQUEST_EXTERNAL_URL_GROUP]: (state: ExternalUrlGroupsFormState,
    action: RequestExternalUrlGroup): ExternalUrlGroupsFormState => {

    return {
      ...state,
      externalUrlGroupItem: undefined,
      originalExternalUrlGroupItem: undefined,
      fetching: true,
      saving: false,
      validation: {}
    };
  },

  [ACTION.RECEIVE_EXTERNAL_URL_GROUP]: (state: ExternalUrlGroupsFormState,
    action: ReceiveExternalUrlGroup): ExternalUrlGroupsFormState => {

    return {
      ...state,
      externalUrlGroupItem: action.item,
      originalExternalUrlGroupItem: action.item,
      fetching: false
    };
  },

  [ACTION.FAIL_EXTERNAL_URL_GROUP]: (state: ExternalUrlGroupsFormState,
    action: FailExternalUrlGroup): ExternalUrlGroupsFormState => {

    return {
      ...state,
      fetching: false
    };
  },

  [ACTION.REQUEST_DELETE_EXTERNAL_URL_GROUP]: (state: ExternalUrlGroupsFormState,
    action: RequestDeleteExternalUrlGroup): ExternalUrlGroupsFormState => {

    return {
      ...state,
      deleting: true
    };
  },

  [ACTION.RECEIVE_DELETE_EXTERNAL_URL_GROUP]: (state: ExternalUrlGroupsFormState,
    action: ReceiveDeleteExternalUrlGroup): ExternalUrlGroupsFormState => {

    return {
      ...state,
      deleting: false
    };
  },

  [ACTION.FAIL_DELETE_EXTERNAL_URL_GROUP]: (state: ExternalUrlGroupsFormState,
    action: FailDeleteExternalUrlGroup): ExternalUrlGroupsFormState => {

    return {
      ...state,
      deleting: false
    };
  },

  [ACTION.REQUEST_SAVE_EXTERNAL_URL_GROUP]: (state: ExternalUrlGroupsFormState,
    action: RequestSaveExternalUrlGroup): ExternalUrlGroupsFormState => {

    return {
      ...state,
      saving: true
    };
  },

  [ACTION.RECEIVE_SAVE_EXTERNAL_URL_GROUP]: (state: ExternalUrlGroupsFormState,
    action: ReceiveSaveExternalUrlGroup): ExternalUrlGroupsFormState => {

    return {
      ...state,
      saving: false,
      originalExternalUrlGroupItem: state.externalUrlGroupItem
    };
  },

  [ACTION.FAIL_SAVE_EXTERNAL_URL_GROUP]: (state: ExternalUrlGroupsFormState,
    action: FailSaveExternalUrlGroup): ExternalUrlGroupsFormState => {

    return {
      ...state,
      saving: false
    };
  },

  [ACTION.CHANGE_LANGUAGE]: (state: ExternalUrlGroupsFormState,
    action: ChangeLanguage): ExternalUrlGroupsFormState => {

    const externalUrlGroupItem = state.externalUrlGroupItem || {} as ExternalUrlGroupItem;
    const language = externalUrlGroupItem.language || [];

    return {
      ...state,
      externalUrlGroupItem: {
        ...externalUrlGroupItem,
        language: language.some(languageItem => languageItem.value === action.languageId)
          ? language.map(
            languageItem => languageItem.value === action.languageId
              ? {
                ...languageItem,
                description: action.description
              }
              : languageItem
          )
          : [
            ...language,
            {
              value: action.languageId,
              description: action.description
            }
          ]
      },
      validation: clearValidationForSingleParameter(state.validation, 'language', languageValidationParameter, action.languageId)
    };
  },

  [ACTION.COPY_LANGUAGE_TO_ALL]: (state: ExternalUrlGroupsFormState,
    action: CopyLanguageToAll): ExternalUrlGroupsFormState => {

    const externalUrlGroupItem = state.externalUrlGroupItem || {} as ExternalUrlGroupItem;
    const language = externalUrlGroupItem.language || [];
    const targetLanguage = language.find(languageItem => languageItem.value === action.languageId);

    if (targetLanguage === undefined) {
      return state;
    }

    const updatedLanguage = language.map(
      languageItem => ({
        ...languageItem,
        description: targetLanguage.description
      })
    );
    const newLanguage = (state.languageOptions || [])
      .map(option => option.value)
      .filter(languageId => !language.some(languageItem => languageItem.value === languageId))
      .map(languageId => ({
        value: languageId,
        description: targetLanguage.description
      }));

    return {
      ...state,
      externalUrlGroupItem: {
        ...externalUrlGroupItem,
        language: [
          ...updatedLanguage,
          ...newLanguage
        ]
      },
      validation: clearValidationForAllParameters(state.validation, 'language', languageValidationParameter)
    };
  },

  [ACTION.CHANGE_LONG_DESCRIPTION]: (state: ExternalUrlGroupsFormState,
    action: ChangeLongDescription): ExternalUrlGroupsFormState => {

    const externalUrlGroupItem = state.externalUrlGroupItem || {} as ExternalUrlGroupItem;
    const longDescription = externalUrlGroupItem.longDescription || [];

    return {
      ...state,
      externalUrlGroupItem: {
        ...externalUrlGroupItem,
        longDescription: longDescription.some(longDescriptionItem => longDescriptionItem.value === action.languageId)
          ? longDescription.map(
            longDescriptionItem => longDescriptionItem.value === action.languageId
              ? {
                ...longDescriptionItem,
                description: action.description
              }
              : longDescriptionItem
          )
          : [
            ...longDescription,
            {
              value: action.languageId,
              description: action.description
            }
          ]
      },
      validation: clearValidationForSingleParameter(
        state.validation,
        'longDescription',
        longDescriptionValidationParameter,
        action.languageId
      )
    };
  },

  [ACTION.COPY_LONG_DESCRIPTION_TO_ALL]: (state: ExternalUrlGroupsFormState,
    action: CopyLongDescriptionToAll): ExternalUrlGroupsFormState => {

    const externalUrlGroupItem = state.externalUrlGroupItem || {} as ExternalUrlGroupItem;
    const longDescription = externalUrlGroupItem.longDescription || [];
    const targetLongDescription = longDescription.find(longDescriptionItem => longDescriptionItem.value === action.languageId);

    if (targetLongDescription === undefined) {
      return state;
    }

    const updatedlongDescription = longDescription.map(
      languageItem => ({
        ...languageItem,
        description: targetLongDescription.description
      })
    );
    const newlongDescription = (state.languageOptions || [])
      .map(option => option.value)
      .filter(languageId => !longDescription.some(languageItem => languageItem.value === languageId))
      .map(languageId => ({
        value: languageId,
        description: targetLongDescription.description
      }));

    return {
      ...state,
      externalUrlGroupItem: {
        ...externalUrlGroupItem,
        longDescription: [
          ...updatedlongDescription,
          ...newlongDescription
        ]
      },
      validation: clearValidationForAllParameters(state.validation, 'longDescription', longDescriptionValidationParameter)
    };
  },

  [ACTION.UPDATE_ITEM]: (state: ExternalUrlGroupsFormState,
    action: UpdateItem): ExternalUrlGroupsFormState => {

    const externalUrlGroupItem = {
      ...(state.externalUrlGroupItem || {}),
      ...action.item
    } as ExternalUrlGroupItem;

    const validation = clearValidationForKeys(state.validation, Object.keys(action.item) as any[]);

    return {
      ...state,
      externalUrlGroupItem: externalUrlGroupItem,
      validation
    };
  },

  [ACTION.CLEAR_FORM]: (state: ExternalUrlGroupsFormState,
    action: ClearForm): ExternalUrlGroupsFormState => {

    return {
      ...state,
      ...INITIAL_STATE,
      languageOptions: state.languageOptions
    };
  },

  [ACTION.UPDATE_VALIDATION]: (state: ExternalUrlGroupsFormState,
    action: UpdateValidation): ExternalUrlGroupsFormState => {
    return {
      ...state,
      validation: { ...state.validation, ...action.validation }
    };
  },

  [ACTION.UPDATE_ACCESS_POLICY_ITEM]: (state: ExternalUrlGroupsFormState,
    action: UpdateAccessPolicyItem): ExternalUrlGroupsFormState => {

    const externalUrlGroupItem = {
      ...(state.externalUrlGroupItem || {}),
      accessPolicy: state.externalUrlGroupItem.accessPolicy.map(item => {

        if (item.id === action.accessPolicyValue.id) {
          return action.accessPolicyValue;
        }

        return item;
      })
    } as ExternalUrlGroupItem;

    return {
      ...state,
      externalUrlGroupItem
    };
  },

  [ACTION.REMOVE_ACCESS_POLICY]: (state: ExternalUrlGroupsFormState,
    action: RemoveAccessPolicy): ExternalUrlGroupsFormState => {

    const externalUrlGroupItem = {
      ...(state.externalUrlGroupItem || {}),
      accessPolicy: state.externalUrlGroupItem.accessPolicy.filter(text => text.id !== action.id) ?? []
    } as ExternalUrlGroupItem;

    return {
      ...state,
      externalUrlGroupItem: externalUrlGroupItem
    };
  },

  [ACTION.REMOVE_ALL_ACCESS_POLICIES]: (state: ExternalUrlGroupsFormState,
    action: RemoveAllAccessPolicies): ExternalUrlGroupsFormState => {

    const externalUrlGroupItem = {
      ...(state.externalUrlGroupItem || {}),
      accessPolicy: []
    } as ExternalUrlGroupItem;

    return {
      ...state,
      externalUrlGroupItem: externalUrlGroupItem
    };
  },

  [ACTION.ADD_ACCESS_POLICY]: (state: ExternalUrlGroupsFormState, action: AddAccessPolicy): ExternalUrlGroupsFormState => {

    const accessPolicy = state.externalUrlGroupItem.accessPolicy ?? [];
    const id = createId(accessPolicy);

    return {
      ...state,
      externalUrlGroupItem: {
        ...state.externalUrlGroupItem,
        accessPolicy: accessPolicy.concat(createInitialAccessPolicy(id))
      }
    };
  },

  [ACTION.UPDATE_ACCESS_POLICY_VALIDATION]: (state: ExternalUrlGroupsFormState,
    action: UpdateAccessPolicyValidation): ExternalUrlGroupsFormState => {

    return {
      ...state,
      accessPolicyValidation: {
        ...state.accessPolicyValidation,
        [action.id]: action.validationProps
      }
    };
  },

  [ACTION.RESET_ACCESS_POLICY_CLIENT_VALIDATION]: (state: ExternalUrlGroupsFormState,
    action: ResetAccessPolicyClientValidation): ExternalUrlGroupsFormState => {

    const messages = state.validation?.[action.key]?.messages?.filter?.(message =>
      message?.parameters?.id !== action.id
    );

    const validation = {
      ...(state.validation ?? {}),
      [action.key]: {
        ...state.validation?.[action.key],
        messages
      }
    };

    return {
      ...state,
      validation: Object.keys(validation)
        .reduce((result, key) => {
          if (!(key === action.key && validation?.[key]?.messages?.length === 0)) {
            result[key] = validation?.[key];
          }
          return result;
        }, {})
    };
  },

  [ACTION.RESET_ACCESS_POLICY_SERVER_VALIDATION]: (state: ExternalUrlGroupsFormState,
    action: ResetAccessPolicyServerValidation): ExternalUrlGroupsFormState => {

    const validationKeys = Object.keys(state.accessPolicyValidation);

    if (!validationKeys.length) {
      return state;
    }

    return {
      ...state,
      accessPolicyValidation: validationKeys
        .filter(item => item !== action.id.toString())
        .reduce((result, key) => {
          result[key] = state.accessPolicyValidation[key];
          return result;
        }, {})
    };
  },

  [ACTION.RESET_ALL_ACCESS_POLICY_VALIDATION]: (state: ExternalUrlGroupsFormState,
    action: ResetAllAccessPolicyValidation): ExternalUrlGroupsFormState => {
    return {
      ...state,
      accessPolicyValidation: undefined,
      validation: {
        ...state.validation,
        caseType: undefined,
        operation: undefined
      }
    };
  },

  [ACTION.REQUEST_ADD_EXTERNAL_URLS]: (
    state: ExternalUrlGroupsFormState,
    action: RequestAddExternalUrls
  ): ExternalUrlGroupsFormState => {

    return {
      ...state,
      fetchingAddExternalUrls: true
    };
  },

  [ACTION.RECEIVE_ADD_EXTERNAL_URLS]: (
    state: ExternalUrlGroupsFormState,
    action: ReceiveAddExternalUrls
  ): ExternalUrlGroupsFormState => {

    return {
      ...state,
      externalUrlGroupItem: {
        ...state.externalUrlGroupItem,
        externalUrls: action.addType === AddExternalUrlType.ITEM_SEQUENCE
          ? action.normalizeSequence(
            !state.externalUrlGroupItem?.externalUrls || state.externalUrlGroupItem?.externalUrls?.length === 0
              ? action.externalUrls.map((url, index) => ({
                ...url,
                sequence: action.sequence + index
              }))
              : [
                ...state.externalUrlGroupItem?.externalUrls
                  .filter(url => url.sequence < action.sequence),
                ...action.externalUrls.map((url, index) => ({
                  ...url,
                  sequence: action.sequence + index
                })),
                ...state.externalUrlGroupItem?.externalUrls
                  .filter(url => url.sequence >= action.sequence)
                  .map((url) => ({
                    ...url,
                    sequence: url.sequence + action.externalUrls.length
                  }))
              ])
          : action.externalUrls.map(externalUrl => ({
            ...externalUrl,
            urlTemplate: state.externalUrlOptions?.values?.find(item => item.value === externalUrl.code.value)?.urlTemplate
          }))
      },
      fetchingAddExternalUrls: false
    };
  },

  [ACTION.FAIL_ADD_EXTERNAL_URLS]: (
    state: ExternalUrlGroupsFormState,
    action: FailAddExternalUrls
  ): ExternalUrlGroupsFormState => {

    return {
      ...state,
      fetchingAddExternalUrls: false
    };
  },

  [ACTION.UPDATE_EXTERNAL_URL_ITEM]: (
    state: ExternalUrlGroupsFormState,
    action: UpdateExternalUrlItem
  ): ExternalUrlGroupsFormState => {

    const id = action.id;
    const stateExternalUrl = state.externalUrlGroupItem.externalUrls ?? [];
    const existingExternalUrl = stateExternalUrl.find(
      item => item.code?.value === id
    );
    const currentExternalUrl = existingExternalUrl?.code?.value === action.externalUrl.code?.value
      ? {
        ...action.externalUrl,
        code: existingExternalUrl?.code
      }
      : action.externalUrl;

    const { [id]: _, ...filteredValidation } = state.exterenalUrlsValidation ?? {};
    const validation = Object.keys(action.validation || {}).length > 0
      ? {
        ...filteredValidation,
        [currentExternalUrl?.code?.value]: action.validation
      }
      : filteredValidation;

    const sequence = currentExternalUrl.sequence ?? 0;

    if (existingExternalUrl?.sequence === sequence) {
      return {
        ...state,
        externalUrlGroupItem: {
          ...state.externalUrlGroupItem,
          externalUrls: [
            ...stateExternalUrl.filter(item => item.sequence < sequence),
            currentExternalUrl,
            ...stateExternalUrl.filter(item => item.sequence >= sequence && item !== existingExternalUrl)
          ]
        },
        exterenalUrlsValidation: validation
      };
    }

    const filteredExternalUrls = existingExternalUrl
      ? action.normalizeSequence(stateExternalUrl.filter(item => item !== existingExternalUrl))
      : stateExternalUrl;

    const externalUrl = [
      ...filteredExternalUrls
        .filter(item => item.sequence < sequence),
      currentExternalUrl,
      ...filteredExternalUrls
        .filter(item => item.sequence >= sequence)
        .map(item => ({
          ...item,
          sequence: item.sequence + 1
        }))
    ];

    return {
      ...state,
      externalUrlGroupItem: {
        ...state.externalUrlGroupItem,
        externalUrls: action.normalizeSequence(externalUrl)
      },
      exterenalUrlsValidation: validation
    };
  },

  [ACTION.DELETE_EXTERNAL_URL]: (
    state: ExternalUrlGroupsFormState,
    action: DeleteExternalUrl
  ): ExternalUrlGroupsFormState => {

    const externalUrlItem = state.externalUrlGroupItem.externalUrls?.find(item => item.code?.value === action.externalUrlId);

    if (!externalUrlItem) {
      return state;
    }

    const externalUrls = state.externalUrlGroupItem.externalUrls
      .filter(item => item !== externalUrlItem);
    const currentExternalUrlId = externalUrlItem.code?.value;
    const { [currentExternalUrlId]: _, ...validation } = state.exterenalUrlsValidation;

    return {
      ...state,
      externalUrlGroupItem: {
        ...state.externalUrlGroupItem,
        externalUrls: action.normalizeSequence(externalUrls)
      },
      exterenalUrlsValidation: validation
    };
  },

  [ACTION.DELETE_ALL_EXTERNAL_URLS]: (
    state: ExternalUrlGroupsFormState,
    action: DeleteAllExternalUrls
  ): ExternalUrlGroupsFormState => {

    return {
      ...state,
      externalUrlGroupItem: {
        ...state.externalUrlGroupItem,
        externalUrls: []
      },
      exterenalUrlsValidation: {}
    };
  },

  [ACTION.UPDATE_EXTERNAL_URLS_VALIDATION]: (
    state: ExternalUrlGroupsFormState,
    action: UpdateExternalUrlsValidation
  ): ExternalUrlGroupsFormState => {

    return {
      ...state,
      exterenalUrlsValidation: action.validation
    };
  },

  [ACTION.UPDATE_VALIDATION]: (
    state: ExternalUrlGroupsFormState,
    action: UpdateValidation
  ): ExternalUrlGroupsFormState => {

    return {
      ...state,
      validation: { ...state.validation, ...action.validation }
    };
  }
};

const stateReducer: Reducer<ExternalUrlGroupsFormState> = (
  state: ExternalUrlGroupsFormState = INITIAL_STATE, action: Action) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};

export const reducer = withOptionsReducer(STATE.externalUrlGroups, stateReducer);
